import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { useAuth } from '@/contexts/auth-context'
import { MetricCard } from '@/components/dashboard'
import { useDashboard } from '@/hooks/use-dashboard'
import { DashboardPeriod } from '@/types/dashboard'
import {
  Activity,
  TrendingUp,
  Users,
  Shield,
  Zap,
  BarChart3,
  Clock,
  MessageSquare,
  Target,
  AlertTriangle,
  CheckCircle2,
  Ban,
  Loader2,
  AlertCircle
} from 'lucide-react'



function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`
}

function formatResponseTime(ms: number): string {
  if (ms < 1000) return `${ms.toFixed(0)}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

export function DashboardPage() {
  const { user } = useAuth()
  const [period, setPeriod] = useState<DashboardPeriod>('month')
  const { data, isLoading, error } = useDashboard(period)

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando métricas...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <AlertCircle className="h-12 w-12 text-destructive" />
          <div>
            <h3 className="text-lg font-semibold mb-2">Erro ao carregar métricas</h3>
            <p className="text-sm text-muted-foreground">
              {error instanceof Error ? error.message : 'Ocorreu um erro inesperado'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // No data (should not happen with valid API, but defensive)
  if (!data) {
    return null
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Dashboard - Assistente IA</h1>
        <p className="text-muted-foreground">
          Bem-vindo de volta, {user?.name}! Métricas de atendimento e performance.
        </p>
      </div>

      {/* Period Filter Tabs */}
      <Tabs value={period} onValueChange={(value) => setPeriod(value as DashboardPeriod)} className="space-y-4">
        <TabsList>
          <TabsTrigger value="today">Hoje</TabsTrigger>
          <TabsTrigger value="week">Esta Semana</TabsTrigger>
          <TabsTrigger value="month">Este Mês</TabsTrigger>
        </TabsList>

        {/* Summary Cards - Always visible */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Total de Mensagens"
            value={data.volumeActivity.totalMessages.toLocaleString()}
            description="Mensagens processadas"
            icon={MessageSquare}
          />
          <MetricCard
            title="Total de Conversas"
            value={data.volumeActivity.totalThreads.toLocaleString()}
            description="Threads de atendimento"
            icon={Activity}
          />
          <MetricCard
            title="Taxa de Sucesso"
            value={formatPercentage(data.aiEfficiency.successRate)}
            description="Execuções bem-sucedidas"
            icon={CheckCircle2}
          />
          <MetricCard
            title="Tentativas Bloqueadas"
            value={data.security.totalBlocked}
            description="Acessos não autorizados"
            icon={Shield}
          />
        </div>

        {/* Period-specific content */}
        <TabsContent value="today" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <MetricCard
              title="Remetentes Únicos"
              value={data.volumeActivity.uniqueSenders.toLocaleString()}
              description="Clientes diferentes"
              icon={Users}
            />
            <MetricCard
              title="Tempo Médio de Resposta"
              value={formatResponseTime(data.aiEfficiency.avgResponseTimeMs)}
              description="Velocidade da IA"
              icon={Zap}
            />
            <MetricCard
              title="Média Mensagens/Thread"
              value={data.volumeActivity.avgMessagesPerThread.toFixed(1)}
              description="Interações por conversa"
              icon={MessageSquare}
            />
          </div>
        </TabsContent>

        <TabsContent value="week" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <MetricCard
              title="Remetentes Únicos"
              value={data.volumeActivity.uniqueSenders.toLocaleString()}
              description="Clientes diferentes"
              icon={Users}
            />
            <MetricCard
              title="Tempo Médio de Resposta"
              value={formatResponseTime(data.aiEfficiency.avgResponseTimeMs)}
              description="Velocidade da IA"
              icon={Zap}
            />
            <MetricCard
              title="Total de Execuções IA"
              value={data.aiEfficiency.totalExecutions.toLocaleString()}
              description="Processamentos de IA"
              icon={Target}
            />
          </div>
        </TabsContent>

        <TabsContent value="month" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <MetricCard
              title="Remetentes Únicos"
              value={data.volumeActivity.uniqueSenders.toLocaleString()}
              description="Clientes diferentes"
              icon={Users}
            />
            <MetricCard
              title="Total Execuções IA"
              value={data.aiEfficiency.totalExecutions.toLocaleString()}
              description="Processamentos de IA"
              icon={TrendingUp}
            />
            <MetricCard
              title="Chamadas de API"
              value={data.aiEfficiency.totalApiCalls.toLocaleString()}
              description="Integrações externas"
              icon={Zap}
            />
          </div>
        </TabsContent>
      </Tabs>

      <Separator />

      {/* Volume e Atividade Section */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">Volume e Atividade</h2>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Distribuição por Horário
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardTitle>
              <CardDescription>Mensagens por hora do dia</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(data.volumeActivity.hourlyDistribution)
                  .sort(([a], [b]) => Number(b) - Number(a))
                  .slice(0, 5)
                  .map(([hour, count]) => {
                    const maxCount = Math.max(...Object.values(data.volumeActivity.hourlyDistribution))
                    const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0
                    return (
                      <div key={hour} className="space-y-1">
                        <div className="flex justify-between items-center text-sm">
                          <span className="font-medium">{hour}:00</span>
                          <span className="text-muted-foreground">{count} mensagens</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div
                            className="bg-primary rounded-full h-2 transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    )
                  })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Métricas de Conversação
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
              </CardTitle>
              <CardDescription>Estatísticas de threads</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Total de threads</span>
                  <span className="text-lg font-bold">
                    {data.volumeActivity.totalThreads.toLocaleString()}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Média mensagens/thread</span>
                  <span className="text-lg font-bold">
                    {data.volumeActivity.avgMessagesPerThread.toFixed(1)}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Remetentes únicos</span>
                  <span className="text-lg font-bold">
                    {data.volumeActivity.uniqueSenders.toLocaleString()}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Separator />

      {/* Eficiência da IA Section */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">Eficiência da IA</h2>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Chamadas de API por Endpoint
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardTitle>
              <CardDescription>Distribuição de integrações</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(data.aiEfficiency.apiCallsByEndpoint)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 5)
                  .map(([endpoint, count]) => {
                    const percentage = data.aiEfficiency.totalApiCalls > 0
                      ? (count / data.aiEfficiency.totalApiCalls) * 100
                      : 0
                    return (
                      <div key={endpoint} className="space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium truncate max-w-[200px]" title={endpoint}>
                            {endpoint}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">
                              {count}
                            </span>
                            <Badge variant="outline">{formatPercentage(percentage)}</Badge>
                          </div>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div
                            className="bg-primary rounded-full h-2 transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    )
                  })}
                {Object.keys(data.aiEfficiency.apiCallsByEndpoint).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Nenhuma chamada de API registrada
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Métricas de Performance
                <Zap className="h-4 w-4 text-muted-foreground" />
              </CardTitle>
              <CardDescription>Indicadores de qualidade</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium">Tempo de Resposta</p>
                    <p className="text-xs text-muted-foreground">Média da IA</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">
                      {formatResponseTime(data.aiEfficiency.avgResponseTimeMs)}
                    </p>
                    <p className="text-xs text-green-600">
                      {data.aiEfficiency.avgResponseTimeMs < 2000 ? 'Excelente' : 'Bom'}
                    </p>
                  </div>
                </div>
                <Separator />
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium">Taxa de Sucesso</p>
                    <p className="text-xs text-muted-foreground">Execuções bem-sucedidas</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">
                      {formatPercentage(data.aiEfficiency.successRate)}
                    </p>
                    <p className="text-xs text-green-600">
                      {data.aiEfficiency.successRate >= 90 ? 'Excelente' : 'Bom'}
                    </p>
                  </div>
                </div>
                <Separator />
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium">Total de Execuções</p>
                    <p className="text-xs text-muted-foreground">Processamentos de IA</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">
                      {data.aiEfficiency.totalExecutions.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {data.aiEfficiency.totalApiCalls} chamadas API
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Separator />

      {/* Segurança & Autorização Section */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">Segurança & Autorização</h2>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Tentativas Bloqueadas Recentes
                <AlertTriangle className="h-4 w-4 text-orange-500" />
              </CardTitle>
              <CardDescription>
                Últimas tentativas não autorizadas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {data.security.recentBlocked.length > 0 ? (
                  data.security.recentBlocked.map((blocked) => (
                    <div
                      key={blocked.id}
                      className="flex justify-between items-start p-3 bg-muted rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">
                            {blocked.senderName || 'Desconhecido'}
                          </p>
                          <Badge variant="destructive">
                            <Ban className="h-3 w-3 mr-1" />
                            Bloqueado
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {blocked.senderPhone || 'Telefone não disponível'}
                        </p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-muted-foreground">
                            {blocked.blockReason}
                          </span>
                          <span className="text-xs text-muted-foreground">•</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(blocked.createdAt).toLocaleString('pt-BR')}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Nenhuma tentativa bloqueada no período
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Estatísticas de Segurança
                <Shield className="h-4 w-4 text-muted-foreground" />
              </CardTitle>
              <CardDescription>Resumo de controle de acesso</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium">Total Bloqueado</p>
                    <p className="text-xs text-muted-foreground">Este período</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">
                      {data.security.totalBlocked}
                    </p>
                    <p className="text-xs text-orange-600">
                      {data.security.totalBlocked > 10 ? 'Requer atenção' : 'Normal'}
                    </p>
                  </div>
                </div>
                <Separator />
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium">Motivos de Bloqueio</p>
                    <p className="text-xs text-muted-foreground">Distribuição</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">
                      {Object.keys(data.security.blockedByReason).length}
                    </p>
                    <p className="text-xs text-muted-foreground">tipos diferentes</p>
                  </div>
                </div>
                <Separator />
                {Object.entries(data.security.blockedByReason)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 3)
                  .map(([reason, count]) => (
                    <div key={reason} className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground truncate max-w-[180px]" title={reason}>
                        {reason}
                      </span>
                      <Badge variant="outline">{count}</Badge>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
